import { useEffect, useState } from "react";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { Icon } from "@/components/ui/Icon";
import { canWriteCalendar, type GoogleCalendarInfo } from "@/domain/googleCalendar";

/**
 * Google 캘린더 가져오기 선택 — 연결 직후(온보딩)와 설정의 [가져오기 추가·변경] 이 같은 창을 쓴다 (2026-10-29).
 * 평면 폼 모달(.modal-backdrop > .modal-panel, 본보기 PersonalEventModal.tsx).
 *
 * 목록 = Google 계정의 캘린더 전체. 체크한 것만 ERP 로 가져온다(서버 set_calendars → 재동기화).
 * 기본 체크: 이미 가져온 것 / 처음이면 Google 화면에서 표시 중인 캘린더(selected).
 */
interface Props {
  email: string;
  calendars: GoogleCalendarInfo[];
  /** 현재 가져온 ID(처음이면 undefined → selected 기준) */
  importedIds?: string[];
  onboarding?: boolean;
  onSave: (ids: string[]) => Promise<void>;
  onClose: () => void;
}

export function GoogleImportModal({ email, calendars, importedIds, onboarding, onSave, onClose }: Props) {
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(importedIds ?? calendars.filter((c) => c.selected).map((c) => c.id)),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const dismiss = useBackdropDismiss(onClose);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (id: string, on: boolean) =>
    setPicked((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });
  const allOn = calendars.length > 0 && calendars.every((c) => picked.has(c.id));

  async function save() {
    setErr("");
    setSaving(true);
    try {
      await onSave([...picked]);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "저장하지 못했습니다.");
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" {...dismiss}>
      <div className="modal-panel my-ev-modal" role="dialog" aria-modal="true" aria-labelledby="my-gimp-title">
        <div className="modal-head">
          <h3 id="my-gimp-title">{onboarding ? "가져올 Google 캘린더 선택" : "Google 캘린더 가져오기"}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="my-ev-body">
          <p className="my-muted my-gimp-intro">
            <b>{email}</b> 계정의 캘린더입니다. 체크한 캘린더의 일정만 ERP 달력에 보이고, 해제하면 ERP 에서만 사라집니다(Google 은 그대로).
          </p>
          <div className="my-gimp-tools">
            <span className="my-muted">{picked.size}/{calendars.length} 선택</span>
            <button
              type="button"
              className="btn-tiny"
              onClick={() => setPicked(allOn ? new Set() : new Set(calendars.map((c) => c.id)))}
              disabled={saving || calendars.length === 0}
            >
              {allOn ? "모두 해제" : "모두 선택"}
            </button>
          </div>
          <ul className="my-list my-gimp-list">
            {calendars.map((c) => (
              <li key={c.id} className="my-row">
                <label className="my-gcal-item">
                  <input type="checkbox" checked={picked.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} disabled={saving} />
                  <i className="my-gcal-dot" style={{ background: c.color || undefined }} aria-hidden="true" />
                  <span className="my-row-title">{c.name}</span>
                </label>
                {c.primary && <span className="my-tag">기본</span>}
                {!canWriteCalendar(c) && <span className="my-tag">읽기 전용</span>}
              </li>
            ))}
          </ul>
          {calendars.length === 0 && <div className="my-block-empty">Google 계정에 캘린더가 없습니다</div>}
          {err && <div className="my-ev-err" role="alert">{err}</div>}
        </div>
        <div className="my-ev-footer">
          <span className="my-ev-spacer" />
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>{onboarding ? "나중에" : "취소"}</button>
          <button type="button" className="btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? "가져오는 중…" : picked.size === 0 ? "가져오지 않음" : `${picked.size}개 가져오기`}
          </button>
        </div>
      </div>
    </div>
  );
}
